import type { IframeProps, ViewContext } from '../custom-ui/iframe/types';
import type { ThemeState } from '@atlaskit/tokens';
import type { CoreDataInner } from '@atlassian/forge-ui-types';
import {
	getResolverContext,
	getViewContext,
	getBrowserTimezone,
	getBrowserLocale,
} from '../custom-ui/iframe/utils';
import { emit } from '../custom-ui/bridge/methods/events';
import type { BridgeContext, BridgeServices } from './types';

export type BridgeContextInput = Pick<
	IframeProps,
	| 'accountId'
	| 'bridge'
	| 'coreData'
	| 'extension'
	| 'extensionData'
	| 'extensionPayload'
	| 'extensionViewData'
	| 'locale'
	| 'timezone'
	| 'contextIds'
	| 'apolloClient'
> & {
	// surfaceColor and theme are on InnerIframeProps but not IframeProps
	surfaceColor?: string | null;
	theme?: Partial<ThemeState> | null;
};

// Registry of which BridgeContext fields emit events on change.
// Keyed on keyof BridgeContext so adding a new field will surface a type error
// if it should also have a change event but doesn't.
export const BRIDGE_CONTEXT_EVENTS = {
	viewContext: 'BRIDGE_VIEW_CONTEXT_CHANGED',
} as const satisfies Partial<Record<keyof BridgeContext, string>>;

// Maps each BridgeContextInput key to whether it changed (reference equality).
// __all__ is true if any key changed.
type Changes = Record<keyof BridgeContextInput, boolean> & { __all__: boolean };

/**
 * Encapsulates the BridgeContext value, its memoization, and change notifications.
 *
 * All memoization is done as plain reference-equality comparisons — no React hooks needed.
 * This makes the logic fully testable without a React tree.
 *
 * Usage:
 *   const obj = new BridgeContextObject(initialProps);
 *   obj.update(latestProps);  // call inside useEffect — diffs and emits events
 *   obj.get();                // call from getBridgeContext getter — always current
 */
export class BridgeContextObject {
	private context: BridgeContext;
	private prevProps: BridgeContextInput;

	constructor(props: BridgeContextInput, services: BridgeServices) {
		this.prevProps = props;
		this.context = { ...this.buildContext(props), services, bridgeConfig: props.bridge ?? {} };
	}

	/**
	 * Diffs incoming props against stored deps, recomputes only what changed,
	 * and emits change events for updated BridgeContext fields.
	 *
	 * Must be called inside useEffect (not during render) because it emits DOM events.
	 */
	update(nextProps: BridgeContextInput): void {
		const changes = this.computeChanges(nextProps);

		if (changes.__all__) {
			const resolverContext = this.computeWhenChanged(
				changes,
				['coreData', 'extensionData', 'extension'],
				this.context.resolverContext,
				() => getResolverContext(
					{ ...nextProps.coreData, appVersion: nextProps.extension.appVersion } as CoreDataInner,
					nextProps.extensionData,
				),
			);

			const viewContext = this.computeWhenChanged(
				changes,
				['accountId', 'coreData', 'extension', 'extensionData', 'extensionPayload', 'extensionViewData', 'locale', 'timezone', 'surfaceColor', 'theme'],
				this.context.viewContext,
				() => this.buildViewContext(nextProps, resolverContext),
			);

			this.context = { ...this.context, resolverContext, viewContext, extensionPayload: nextProps.extensionPayload, bridgeConfig: nextProps.bridge ?? {} };
			emit(BRIDGE_CONTEXT_EVENTS.viewContext, { viewContext: this.context.viewContext });
		}

		this.prevProps = nextProps;
	}

	get(): BridgeContext {
		return this.context;
	}

	private computeChanges(nextProps: BridgeContextInput): Changes {
		const allKeys = new Set([
			...Object.keys(nextProps),
			...Object.keys(this.prevProps),
		]) as Set<keyof BridgeContextInput>;

		const changes = {
			__all__: false,
		} as Changes;
		for (const key of allKeys) {
			const changed = nextProps[key] !== this.prevProps[key];
			changes[key] = changed;
			if (changed) changes.__all__ = true;
		}

		return changes;
	}

	private computeWhenChanged<T>(
		changes: Changes,
		keys: ReadonlyArray<keyof BridgeContextInput>,
		cached: T,
		compute: () => T,
	): T {
		return keys.some((k) => changes[k]) ? compute() : cached;
	}

	private buildContext(props: BridgeContextInput): Pick<BridgeContext, 'resolverContext' | 'viewContext' | 'extensionPayload' | 'bridgeConfig'> {
		const resolverContext = getResolverContext(
			{ ...props.coreData, appVersion: props.extension.appVersion } as CoreDataInner,
			props.extensionData,
		);
		return {
			resolverContext,
			viewContext: this.buildViewContext(props, resolverContext),
			extensionPayload: props.extensionPayload,
			bridgeConfig: props.bridge ?? {},
		};
	}

	private buildViewContext(props: BridgeContextInput, resolverContext: ReturnType<typeof getResolverContext>): ViewContext {
		return getViewContext({
			resolverContext,
			accountId: props.accountId,
			extension: props.extension,
			timezone: props.timezone ?? getBrowserTimezone(),
			locale: props.locale ?? getBrowserLocale(),
			extensionPayload: props.extensionPayload,
			extensionViewData: props.extensionViewData,
			theme: props.theme,
			surfaceColor: props.surfaceColor,
			location: window.location.toString(),
		});
	}
}
